import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Loader2, Save, Upload, X, Plus, Store, Image as ImageIcon, Mail, AlertTriangle } from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";

/**
 * Multi-brand manager inside Invoice Settings > Business Identity.
 * Brands ARE store locations — each location's identity fields drive the
 * branding on invoices/estimates assigned to it (PDF, email, payment page).
 * Renders nothing for single-location accounts (no store_locations docs).
 */

const BrandCard = ({ loc, isDark, headers, onSaved, sender }) => {
  const [form, setForm] = useState({
    business_name: loc.business_name || "",
    business_email: loc.business_email || "",
    phone: loc.phone || "",
    address: loc.address || "",
    city: loc.city || "",
    state: loc.state || "",
    zip_code: loc.zip_code || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(
    loc.logo_file || loc.logo_data
      ? `${API}/invoices/template/location-logo/${loc.location_id}?v=${Date.now()}`
      : null
  );
  const fileRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/store/locations/${loc.location_id}`, form, { headers });
      toast.success(`"${loc.name}" brand saved`);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save brand");
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Logo must be 5MB or smaller");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/invoices/template/location-logo/${loc.location_id}`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      setLogoUrl(`${API}/invoices/template/location-logo/${loc.location_id}?v=${Date.now()}`);
      toast.success("Brand logo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    try {
      await axios.delete(`${API}/invoices/template/location-logo/${loc.location_id}`, { headers });
      setLogoUrl(null);
      toast.success("Brand logo removed");
    } catch {
      toast.error("Failed to remove logo");
    }
  };

  return (
    <div
      className={`rounded-lg border p-3 space-y-3 ${isDark ? "border-zinc-800 bg-zinc-800/40" : "border-gray-200 bg-gray-50/60"}`}
      data-testid={`brand-card-${loc.location_id}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Store className="w-3.5 h-3.5 text-blue-500" />
          <span className="text-sm font-medium">{loc.name}</span>
        </div>
        <Button size="sm" onClick={save} disabled={saving} className="h-7 text-xs" data-testid={`brand-save-${loc.location_id}`}>
          {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
          Save
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={uploadLogo} data-testid={`brand-logo-input-${loc.location_id}`} />
        {logoUrl ? (
          <div className={`rounded-md border p-1 w-16 h-12 flex items-center justify-center overflow-hidden ${isDark ? "border-zinc-700 bg-zinc-800" : "border-gray-200 bg-white"}`}>
            <img src={logoUrl} alt={`${loc.name} logo`} className="max-w-full max-h-full object-contain" />
          </div>
        ) : (
          <div className={`rounded-md border border-dashed w-16 h-12 flex items-center justify-center ${isDark ? "border-zinc-700" : "border-gray-300"}`}>
            <ImageIcon className="w-4 h-4 text-muted-foreground" />
          </div>
        )}
        <div className="flex gap-1.5">
          <Button type="button" size="sm" variant="outline" className="h-7 text-xs" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid={`brand-logo-upload-${loc.location_id}`}>
            {uploading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
            {logoUrl ? "Replace logo" : "Upload logo"}
          </Button>
          {logoUrl && (
            <Button type="button" size="sm" variant="ghost" className="h-7 text-xs text-red-500 hover:text-red-600" onClick={removeLogo} data-testid={`brand-logo-remove-${loc.location_id}`}>
              <X className="w-3 h-3" />
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Business Name</Label>
          <Input value={form.business_name} onChange={(e) => set("business_name", e.target.value)}
            placeholder="Inherits main business name" className="h-8 text-sm" data-testid={`brand-name-${loc.location_id}`} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Email</Label>
          <Input type="email" value={form.business_email} onChange={(e) => set("business_email", e.target.value)}
            placeholder="Inherits main email" className="h-8 text-sm" data-testid={`brand-email-${loc.location_id}`} />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Phone</Label>
          <Input value={form.phone} onChange={(e) => set("phone", e.target.value)}
            placeholder="Inherits main phone" className="h-8 text-sm" data-testid={`brand-phone-${loc.location_id}`} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Street Address</Label>
          <Input value={form.address} onChange={(e) => set("address", e.target.value)}
            placeholder="123 Main St" className="h-8 text-sm" data-testid={`brand-address-${loc.location_id}`} />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">City</Label>
          <Input value={form.city} onChange={(e) => set("city", e.target.value)} className="h-8 text-sm" data-testid={`brand-city-${loc.location_id}`} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">State</Label>
          <Input value={form.state} onChange={(e) => set("state", e.target.value)} className="h-8 text-sm" data-testid={`brand-state-${loc.location_id}`} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">ZIP</Label>
          <Input value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} className="h-8 text-sm" data-testid={`brand-zip-${loc.location_id}`} />
        </div>
      </div>
      <p className={`text-[11px] ${isDark ? "text-zinc-500" : "text-gray-400"}`}>
        Empty fields inherit from your main business identity above.
      </p>

      {/* Email sender for this brand — the actual From address on sends */}
      {sender && sender.status === "verified" ? (
        <div className="flex items-center gap-1.5 text-[11px] text-emerald-500" data-testid={`brand-sender-${loc.location_id}`}>
          <Mail className="w-3 h-3 shrink-0" />
          <span>Emails send from <b>{sender.sender_email}</b> (verified)</span>
        </div>
      ) : sender ? (
        <div className="flex items-center gap-1.5 text-[11px] text-amber-500" data-testid={`brand-sender-${loc.location_id}`}>
          <AlertTriangle className="w-3 h-3 shrink-0" />
          <span>
            Sender <b>{sender.sender_email}</b> is pending verification —{" "}
            <a href="/dashboard/settings?tab=email" className="underline">check Email Settings</a>
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-1.5 text-[11px] text-amber-500" data-testid={`brand-sender-${loc.location_id}`}>
          <AlertTriangle className="w-3 h-3 shrink-0" />
          <span>
            No email sender assigned — invoice &amp; estimate emails from this brand are blocked.{" "}
            <a href="/dashboard/settings?tab=email" className="underline">Assign one in Email Settings</a>
          </span>
        </div>
      )}
    </div>
  );
};

const BrandIdentityManager = ({ isDark, headers }) => {
  const [locations, setLocations] = useState(null);
  const [limit, setLimit] = useState(null);
  const [senders, setSenders] = useState([]);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const fetchAll = async () => {
    try {
      const [locs, lim, ids] = await Promise.all([
        axios.get(`${API}/store/locations`, { headers }),
        axios.get(`${API}/store/location-limit-status`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/sender-identities/all`, { headers }).catch(() => ({ data: { identities: [] } })),
      ]);
      setLocations(locs.data?.locations || []);
      setLimit(lim.data);
      setSenders(ids.data?.identities || []);
    } catch {
      setLocations([]);
    }
  };

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!locations || locations.length === 0) return null; // single-location accounts: unchanged UI

  const unlimited = limit?.limit === -1;
  const atLimit = limit && !unlimited && (limit.current ?? locations.length) >= (limit.limit ?? 1);

  const createBrand = async () => {
    const name = newName.trim();
    if (!name) {
      toast.error("Enter a business name");
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/store/locations`, { name, business_name: name }, { headers });
      toast.success(`"${name}" added — fill in its identity below`);
      setNewName("");
      setAdding(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Your plan's business limit has been reached");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-3 pt-4 mt-4 border-t border-dashed" data-testid="brand-identity-manager">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sm font-medium">Your Businesses</Label>
          <p className={`text-xs ${isDark ? "text-zinc-500" : "text-gray-400"}`}>
            Each brand's identity is used on estimates &amp; invoices assigned to it — pick the brand in the "Business / Brand" dropdown when creating a document.
          </p>
        </div>
        {limit && (
          <span className={`text-[11px] px-2 py-0.5 rounded-full shrink-0 ${isDark ? "bg-zinc-800 text-zinc-400" : "bg-gray-100 text-gray-500"}`} data-testid="brand-limit-badge">
            {unlimited ? `${locations.length} businesses` : `${limit.current ?? locations.length} of ${limit.limit} used`}
          </span>
        )}
      </div>

      <div className="space-y-3">
        {locations.map((loc) => (
          <BrandCard
            key={loc.location_id}
            loc={loc}
            isDark={isDark}
            headers={headers}
            onSaved={fetchAll}
            sender={senders.find((s) => s.location_id === loc.location_id) || null}
          />
        ))}
      </div>

      {adding ? (
        <div className="flex items-center gap-2" data-testid="brand-add-form">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New business name (e.g. Acme Garage Doors)"
            className="h-9 text-sm"
            autoFocus
            onKeyDown={(e) => e.key === "Enter" && createBrand()}
            data-testid="brand-add-name-input"
          />
          <Button size="sm" onClick={createBrand} disabled={creating} data-testid="brand-add-confirm-btn">
            {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Add"}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setNewName(""); }} data-testid="brand-add-cancel-btn">
            Cancel
          </Button>
        </div>
      ) : atLimit ? (
        <p className={`text-xs ${isDark ? "text-zinc-500" : "text-gray-400"}`} data-testid="brand-limit-note">
          You've used all {limit.limit} businesses included in your plan. Upgrade your plan to add more.
        </p>
      ) : (
        <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="brand-add-btn">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Business
        </Button>
      )}
    </div>
  );
};

export default BrandIdentityManager;
